/**
 * Time Tracking.
 *
 * Two views behind one nav item, chosen by role rather than by a toggle:
 * everyone sees their own timesheet; a manager additionally sees the team's
 * with cost and the approval queue. The data comes from different endpoints —
 * /service/my-time carries no cost columns at all.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type TimeEntry } from '@/lib/api';
import { useAuth } from '@/features/auth/auth-context';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Check, X } from 'lucide-react';

const CLASS_LABEL: Record<string, string> = {
  included: 'In subscription',
  billable: 'Billable extra',
  nonbillable: 'Non-billable',
  internal: 'Internal',
};

function classVariant(c: string): 'default' | 'secondary' | 'outline' {
  if (c === 'billable') return 'default';
  if (c === 'included') return 'secondary';
  return 'outline';
}

export function ServiceTimePage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('approvals');
  const isManager = user?.role === 'admin' || user?.role === 'manager';

  if (!isManager) {
    return (
      <div className="p-6 space-y-6 max-w-4xl">
        <Header />
        <MyTimesheet />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <Header />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="approvals">Approvals</TabsTrigger>
          <TabsTrigger value="team">Team time</TabsTrigger>
          <TabsTrigger value="rates">Rates</TabsTrigger>
          <TabsTrigger value="mine">My timesheet</TabsTrigger>
        </TabsList>
        <TabsContent value="approvals" className="mt-4"><Approvals /></TabsContent>
        <TabsContent value="team" className="mt-4"><TeamTime /></TabsContent>
        <TabsContent value="rates" className="mt-4"><LabourRates /></TabsContent>
        <TabsContent value="mine" className="mt-4"><MyTimesheet /></TabsContent>
      </Tabs>
    </div>
  );
}

function Header() {
  return (
    <div>
      <h1 className="text-2xl font-serif font-semibold tracking-tight">Time Tracking</h1>
      <p className="text-sm text-muted-foreground mt-1">
        Logged in minutes against a task, then classified and approved.
      </p>
    </div>
  );
}

function MyTimesheet() {
  const { data, isLoading } = useQuery({
    queryKey: ['svc', 'my-time'],
    queryFn: () => api.getMyTime(),
  });
  const rows = (data?.data ?? []) as TimeEntry[];
  const summary = (data as { summary?: { total_hours: number; pending_count: number } } | undefined)?.summary;

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">
          My timesheet
          {summary && (
            <span className="ml-2 font-normal text-xs text-muted-foreground">
              {Number(summary.total_hours ?? 0).toFixed(2)}h total · {summary.pending_count ?? 0} awaiting approval
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <p className="px-6 py-10 text-center text-sm text-muted-foreground">
            No time logged yet. Log it from a task on My Work.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Task</TableHead>
                <TableHead>Client</TableHead>
                <TableHead className="text-right">Hours</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Approval</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="text-sm">{formatDate(t.work_date)}</TableCell>
                  <TableCell className="text-sm max-w-xs truncate">
                    {t.work_item_title ?? t.description ?? '—'}
                  </TableCell>
                  <TableCell className="text-sm">{t.account_name ?? '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">{Number(t.hours).toFixed(2)}</TableCell>
                  <TableCell>
                    <Badge variant={classVariant(t.billable_class)}>
                      {CLASS_LABEL[t.billable_class] ?? t.billable_class}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={
                      t.approval_status === 'approved' ? 'default'
                      : t.approval_status === 'rejected' ? 'destructive' : 'outline'
                    }>
                      {t.approval_status}
                    </Badge>
                    {t.rejected_reason && (
                      <p className="text-xs text-destructive mt-0.5">{t.rejected_reason}</p>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function Approvals() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['svc', 'time-entries', 'pending'],
    queryFn: () => api.getTimeEntries({ approvalStatus: 'pending' }),
  });

  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: string }) =>
      api.decideTimeEntry(id, { decision }),
    onSuccess: (r) => {
      if (!r.success) {
        toast({ title: 'Could not record the decision', description: r.error?.message, variant: 'destructive' });
        return;
      }
      qc.invalidateQueries({ queryKey: ['svc'] });
      toast({ title: 'Recorded' });
    },
  });

  const rows = (data?.data ?? []) as TimeEntry[];
  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <Card>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <p className="px-6 py-10 text-center text-sm text-muted-foreground">
            No time awaiting approval.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Who</TableHead>
                <TableHead>Task</TableHead>
                <TableHead className="text-right">Hours</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="text-sm">{formatDate(t.work_date)}</TableCell>
                  <TableCell className="text-sm">{t.user_name}</TableCell>
                  <TableCell className="text-sm max-w-xs truncate">
                    {t.work_item_title ?? t.description ?? '—'}
                    <span className="block text-xs text-muted-foreground">{t.account_name}</span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{Number(t.hours).toFixed(2)}</TableCell>
                  <TableCell>
                    <Badge variant={classVariant(t.billable_class)}>
                      {CLASS_LABEL[t.billable_class] ?? t.billable_class}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm">
                    {t.labor_cost != null ? formatCurrency(t.labor_cost) : '—'}
                    {t.cost_rate_basis === 'wage_plus_burden' && (
                      <span className="block text-[11px] text-amber-700 dark:text-amber-500">est.</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => decide.mutate({ id: t.id, decision: 'approved' })}
                        disabled={decide.isPending}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => decide.mutate({ id: t.id, decision: 'rejected' })}
                        disabled={decide.isPending}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function TeamTime() {
  const { data, isLoading } = useQuery({
    queryKey: ['svc', 'time-entries', 'all'],
    queryFn: () => api.getTimeEntries({ limit: 100 }),
  });
  const rows = (data?.data ?? []) as TimeEntry[];
  const summary = (data as { summary?: { total_hours: number; total_labor_cost: number } } | undefined)?.summary;

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">
          Team time
          {summary && (
            <span className="ml-2 font-normal text-xs text-muted-foreground">
              {Number(summary.total_hours ?? 0).toFixed(2)}h · {formatCurrency(summary.total_labor_cost ?? 0)} direct labour
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Who</TableHead>
              <TableHead>Client</TableHead>
              <TableHead className="text-right">Hours</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead>Approval</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((t) => (
              <TableRow key={t.id}>
                <TableCell className="text-sm">{formatDate(t.work_date)}</TableCell>
                <TableCell className="text-sm">{t.user_name}</TableCell>
                <TableCell className="text-sm">{t.account_name ?? '—'}</TableCell>
                <TableCell className="text-right tabular-nums">{Number(t.hours).toFixed(2)}</TableCell>
                <TableCell>
                  <Badge variant={classVariant(t.billable_class)}>
                    {CLASS_LABEL[t.billable_class] ?? t.billable_class}
                  </Badge>
                </TableCell>
                <TableCell className="text-right tabular-nums text-sm">
                  {t.labor_cost != null ? formatCurrency(t.labor_cost) : '—'}
                </TableCell>
                <TableCell>
                  <Badge variant={t.approval_status === 'approved' ? 'default' : 'outline'}>
                    {t.approval_status}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

/**
 * Labour rates.
 *
 * Until someone has a rate, their delivery hours cost zero — which makes every
 * cycle they touch look like 100% margin. The figures withhold themselves in
 * that state rather than print the flattering number, so this screen is what
 * turns margin reporting on.
 *
 * A new rate closes the previous open-ended one the day before it starts, so
 * ranges never overlap and a past entry keeps the rate it was priced at.
 */
function LabourRates() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [userId, setUserId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [wageRate, setWageRate] = useState('');
  const [burdenPct, setBurdenPct] = useState('0.25');
  const [loadedCostRate, setLoadedCostRate] = useState('');
  const [billingRate, setBillingRate] = useState('');

  const { data: usersData } = useQuery({
    queryKey: ['users', 'for-rates'],
    queryFn: () => api.listUsers({ limit: 100 }),
    retry: false,
  });
  const users = ((usersData?.data ?? []) as Array<{
    id: string; firstName: string; lastName: string; role: string; isActive: boolean;
  }>).filter((u) => u.isActive);

  const save = useMutation({
    mutationFn: () =>
      api.upsertLaborRate({
        userId,
        effectiveFrom,
        wageRate: wageRate === '' ? null : Number(wageRate),
        burdenPct: Number(burdenPct) || 0,
        loadedCostRate: loadedCostRate === '' ? null : Number(loadedCostRate),
        defaultBillingRate: billingRate === '' ? null : Number(billingRate),
      }),
    onSuccess: (r) => {
      if (!r.success) {
        toast({ title: 'Could not save the rate', description: r.error?.message, variant: 'destructive' });
        return;
      }
      qc.invalidateQueries({ queryKey: ['svc'] });
      toast({ title: 'Rate saved', description: 'Applies to time logged from the effective date onward.' });
      setWageRate(''); setLoadedCostRate(''); setBillingRate('');
    },
  });

  const effectiveCost =
    loadedCostRate !== '' ? Number(loadedCostRate)
    : wageRate !== '' ? Number(wageRate) * (1 + (Number(burdenPct) || 0))
    : null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Set a labour rate</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 max-w-2xl">
        <p className="text-xs text-muted-foreground">
          Rates are snapshotted onto each time entry when it is logged, so changing one here
          never re-prices work already done.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs">Person</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.firstName} {u.lastName} ({u.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Effective from</Label>
            <Input
              className="mt-1" type="date" value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
            />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <Label className="text-xs">Hourly wage</Label>
            <Input
              className="mt-1" type="number" min={0} step="0.01"
              value={wageRate} onChange={(e) => setWageRate(e.target.value)}
              placeholder="20.00"
            />
          </div>
          <div>
            <Label className="text-xs">Burden</Label>
            <Input
              className="mt-1" type="number" min={0} max={5} step="0.01"
              value={burdenPct} onChange={(e) => setBurdenPct(e.target.value)}
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              0.25 = +25% for payroll taxes etc.
            </p>
          </div>
          <div>
            <Label className="text-xs">Loaded cost (optional)</Label>
            <Input
              className="mt-1" type="number" min={0} step="0.01"
              value={loadedCostRate} onChange={(e) => setLoadedCostRate(e.target.value)}
              placeholder="overrides wage × burden"
            />
          </div>
        </div>

        <div className="sm:max-w-xs">
          <Label className="text-xs">Default billing rate (optional)</Label>
          <Input
            className="mt-1" type="number" min={0} step="0.01"
            value={billingRate} onChange={(e) => setBillingRate(e.target.value)}
            placeholder="for authorized extra work"
          />
        </div>

        {effectiveCost != null && (
          <p className="text-xs text-muted-foreground border-t pt-3">
            Effective cost: <strong>{formatCurrency(effectiveCost)}/hour</strong>
            {loadedCostRate !== ''
              ? ' (loaded rate — treated as precise)'
              : ' (wage × burden — margin figures will be marked an estimate)'}
          </p>
        )}

        <Button
          onClick={() => save.mutate()}
          disabled={!userId || (wageRate === '' && loadedCostRate === '') || save.isPending}
        >
          Save rate
        </Button>
      </CardContent>
    </Card>
  );
}
